import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, ArrowDown } from "lucide-react";
import Calculator from "@/pages/Calculator";

export default function Landing() {
  useEffect(() => {
    document.title = "Glydelabs.co — Operational Overhead: The Hidden Cost of Running the Business";
  }, []);
  return (
    <div>
      {/* Hero — full viewport, centered, off-white */}
      <section className="relative min-h-[100dvh] bg-[#f5f4f0] flex flex-col overflow-hidden">
        <img
          src="/hero-ocean.webp"
          alt=""
          aria-hidden
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-90 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white pointer-events-none" />
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p
            data-testid="hero-label"
            className="reveal font-mono-x text-[11px] md:text-[12.6px] uppercase tracking-[1.6px] text-[#5e5e5e]"
            style={{ animationDelay: "0s" }}
          >
            GLYDELABS · Early Research
          </p>

          <h1
            data-testid="hero-headline"
            className="reveal font-display mt-8 text-[#131313] text-balance"
            style={{
              animationDelay: "0.18s",
              fontSize: "clamp(40px, 5.2vw, 64px)",
              lineHeight: 1.03,
              letterSpacing: "-1.6px",
              maxWidth: 1000,
            }}
          >
            <span className="block">The hidden cost of running the business:</span>
            <span className="block text-[#e8720c]">Operational Overhead</span>
          </h1>
        </div>

        <div
          className="reveal relative z-10 pb-10 flex flex-col items-center gap-2"
          style={{ animationDelay: "1.08s" }}
        >
          <ChevronDown className="h-4 w-4 text-[#9c9c9c]" />
        </div>
      </section>

      {/* Stat section — white rounded sheet over the hero */}
      <section className="relative z-10 bg-white rounded-t-[30px] -mt-8 shadow-[0_-30px_60px_rgba(0,0,0,0.04)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-10 items-start">
            <div
              className="reveal text-center lg:text-left lg:self-stretch lg:flex lg:flex-col"
            >
              <h2
                data-testid="stat-headline"
                className="font-display text-[#5e5e5e]"
              >
                <span
                  className="text-[#131313] align-baseline"
                  style={{ fontSize: "clamp(56px, 6.5vw, 88px)", lineHeight: 1, letterSpacing: "-2.5px", marginRight: 14 }}
                >
                  35–60%
                </span>
                <span style={{ fontSize: "clamp(22px, 2.4vw, 32px)", lineHeight: 1.15, letterSpacing: "-0.8px" }}>
                  of work time, gone to coordination.
                </span>
              </h2>
              <p className="mt-6 font-mono-x text-[10px] uppercase tracking-[2px] text-[#9c9c9c]">
                McKinsey &amp; Company — "Cutting the Coordination Tax", 2026
              </p>

              <div className="mt-12 lg:mt-32 flex items-center gap-3 justify-center lg:justify-end lg:pr-6">
                <span
                  data-testid="compare-phrase"
                  className="font-display italic text-[#131313]"
                  style={{ fontSize: "clamp(22px, 2.2vw, 28px)", letterSpacing: "-0.5px" }}
                >
                  How does yours compare?
                </span>
                <ArrowRight className="hidden lg:block h-6 w-6 text-[#e8720c]" />
                <ArrowDown className="lg:hidden h-6 w-6 text-[#e8720c]" />
              </div>
            </div>

            <div
              className="reveal"
              style={{ animationDelay: "0.15s" }}
              data-testid="calculator-section"
            >
              <Calculator embedded />
            </div>
          </div>
        </div>

        {/* CTA band */}
        <div className="border-t border-[#e8e8e6]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
            <h2
              data-testid="landing-cta-headline"
              className="reveal font-display text-[#131313] text-balance mx-auto"
              style={{
                fontSize: "clamp(32px, 4vw, 48px)",
                lineHeight: 1.08,
                letterSpacing: "-1.2px",
                maxWidth: 720,
              }}
            >
              Interested in reducing your Operational Overhead?
            </h2>
            <div
              className="reveal mt-10"
              style={{ animationDelay: "0.15s" }}
            >
              <Link to="/register">
                <button
                  data-testid="landing-cta-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#131313] text-white px-10 py-4 font-mono-x text-[11px] uppercase tracking-[1.6px] transition-transform duration-200 hover:scale-[1.03] active:scale-95"
                >
                  Get Early Access
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
